import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel,
  SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Split as SplitIcon } from "lucide-react";
import { formatCurrency } from "@/lib/format";

/**
 * SplitTransferDialog (iter 72r)
 *
 * Move funds from ONE source wallet/investment into N destinations in a
 * single flow. Each row has its own destination + amount + optional memo.
 * Backend endpoint: POST /api/transfers/split — see
 * /app/backend/server.py (SplitTransferIn model).
 *
 * Partial failures are surfaced per-row: the response returns
 * results=[{index, ok, error?, transfer_id?}]; we show a per-row error
 * chip so the user can retry only the failed legs.
 */
export default function SplitTransferDialog({ open, onClose, onDone }) {
  const [accounts, setAccounts] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [fromVal, setFromVal] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [memo, setMemo] = useState("");
  const [rows, setRows] = useState([{ _key: `r${Date.now()}`, toVal: "", amount: "", memo: "", err: null }]);
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [{ data: accs }, { data: invs }] = await Promise.all([
          api.get("/accounts"),
          api.get("/investments"),
        ]);
        setAccounts(accs || []);
        setInvestments(invs || []);
      } catch (e) { /* non-blocking */ }
    })();
    setFromVal("");
    setMemo("");
    setDate(new Date().toISOString().slice(0, 10));
    setRows([{ _key: `r${Date.now()}`, toVal: "", amount: "", memo: "", err: null }]);
  }, [open]);

  const decode = (v) => {
    if (!v) return null;
    const i = v.indexOf(":");
    return { kind: v.slice(0, i), id: v.slice(i + 1) };
  };
  const encode = (k, id) => `${k}:${id}`;

  const from = useMemo(() => decode(fromVal), [fromVal]);
  const fromRec = useMemo(() => {
    if (!from) return null;
    return from.kind === "wallet"
      ? accounts.find((a) => a.id === from.id)
      : investments.find((i) => i.id === from.id);
  }, [from, accounts, investments]);
  const ccy = fromRec?.currency || "USD";

  const total = useMemo(() =>
    rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0)
  , [rows]);

  const addRow = () => setRows((r) => [...r, { _key: `r${Date.now()}_${r.length}`, toVal: "", amount: "", memo: "", err: null }]);
  const removeRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i));
  const setRow = (i, patch) =>
    setRows((r) => r.map((row, idx) => (idx === i ? { ...row, ...patch, err: null } : row)));

  const submit = async () => {
    if (!from) return toast.error("Pick a source");
    const allocations = rows
      .filter((r) => r.toVal && (parseFloat(r.amount) || 0) > 0)
      .map((r) => {
        const to = decode(r.toVal);
        return { to, amount: parseFloat(r.amount), memo: r.memo || "" };
      });
    if (allocations.length === 0)
      return toast.error("Add at least one destination with an amount");
    if (allocations.some((a) => a.to.kind === from.kind && a.to.id === from.id))
      return toast.error("A destination can't be the same as the source");

    setPosting(true);
    try {
      const { data } = await api.post("/transfers/split", {
        from, date, memo, allocations,
      });
      // Mark failed rows visibly, dismiss on success.
      const nextRows = rows.map((r) => ({ ...r, err: null }));
      let posted = 0;
      let failed = 0;
      data.results.forEach((res, i) => {
        // Match to the visible row order (skipping empty ones that were filtered out)
        const visibleRow = rows.filter((r) => r.toVal && (parseFloat(r.amount) || 0) > 0)[i];
        if (visibleRow) {
          const rowIdx = rows.indexOf(visibleRow);
          if (res.ok) posted += 1;
          else { failed += 1; nextRows[rowIdx].err = res.error || "failed"; }
        }
      });
      setRows(nextRows);
      if (data.ok) {
        toast.success(`Split posted · ${posted} destination${posted === 1 ? "" : "s"}`);
        onDone && onDone();
      } else {
        toast.error(`${posted} posted, ${failed} failed — see rows for details`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Split transfer failed");
    } finally { setPosting(false); }
  };

  const sourceOptions = () => (
    <>
      <SelectGroup>
        <SelectLabel>Wallets & Credit Cards</SelectLabel>
        {accounts.map((a) => (
          <SelectItem key={a.id} value={encode("wallet", a.id)}
                      data-testid={`split-from-wallet-${a.id}`}>
            {a.name} · {a.currency}
          </SelectItem>
        ))}
      </SelectGroup>
      {investments.length > 0 && (
        <SelectGroup>
          <SelectLabel>Investments</SelectLabel>
          {investments.map((i) => (
            <SelectItem key={i.id} value={encode("investment", i.id)}
                        data-testid={`split-from-inv-${i.id}`}>
              {i.symbol || i.name} · {i.currency}
            </SelectItem>
          ))}
        </SelectGroup>
      )}
    </>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent data-testid="split-transfer-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <SplitIcon className="w-4 h-4" /> Split transfer
          </DialogTitle>
          <DialogDescription>
            Move money from one account into multiple destinations in a single
            flow. Each row posts as its own transfer — failures on any row
            don't roll back the ones that already succeeded.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>From</Label>
            <Select value={fromVal} onValueChange={setFromVal}>
              <SelectTrigger data-testid="split-from"><SelectValue placeholder="Pick source" /></SelectTrigger>
              <SelectContent>{sourceOptions()}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                   data-testid="split-date" />
          </div>
        </div>

        <div className="mt-3">
          <Label>Global memo (optional)</Label>
          <Input value={memo} onChange={(e) => setMemo(e.target.value)}
                 data-testid="split-memo" placeholder="e.g. Feb 15 salary allocation" />
        </div>

        {/* Allocation rows */}
        <div className="mt-4 space-y-2 max-h-[280px] overflow-y-auto pr-1">
          {rows.map((r, i) => (
            <div key={r._key || `row-${i}`} data-testid={`split-row-${i}`} className="grid grid-cols-[1fr_120px_1fr_36px] gap-2 items-start">
              <Select value={r.toVal} onValueChange={(v) => setRow(i, { toVal: v })}>
                <SelectTrigger data-testid={`split-to-${i}`} className="text-sm">
                  <SelectValue placeholder="Destination" />
                </SelectTrigger>
                <SelectContent>{sourceOptions()}</SelectContent>
              </Select>
              <Input type="number" step="0.01" min="0"
                     placeholder={`Amount (${ccy})`}
                     value={r.amount} onChange={(e) => setRow(i, { amount: e.target.value })}
                     data-testid={`split-amount-${i}`} className="font-mono" />
              <Input placeholder="Row memo (optional)" value={r.memo}
                     onChange={(e) => setRow(i, { memo: e.target.value })}
                     data-testid={`split-row-memo-${i}`} />
              <Button variant="ghost" size="sm" className="text-[#B4283A] hover:bg-[#FCE7E9]"
                      onClick={() => removeRow(i)} disabled={rows.length === 1}
                      data-testid={`split-row-remove-${i}`}
                      title="Remove row">
                <Trash2 className="w-4 h-4" />
              </Button>
              {r.err && (
                <div className="col-span-4 text-[11px] text-[#B4283A]" data-testid={`split-row-error-${i}`}>
                  {r.err}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between mt-2">
          <Button variant="outline" size="sm" onClick={addRow} data-testid="split-add-row">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Add destination
          </Button>
          <div className="text-sm text-[var(--muted-text)]" data-testid="split-total">
            Total: <span className="font-mono">{formatCurrency(total, ccy)}</span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={posting || !from}
                  className="btn-brand" data-testid="split-submit">
            {posting
              ? <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Posting…</>
              : <>Post {rows.filter((r) => r.toVal && (parseFloat(r.amount) || 0) > 0).length} transfer{rows.length === 1 ? "" : "s"}</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
